import { useContext, useEffect, useState } from "react";
import { Typography, Box } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useTranslation } from "react-i18next";

import ApplicationListComponent from "./application-list/application-list";
import AddApplicationComponent from "./add/add-application";
import DeleteApplicationComponent from "./delete/delete-application";
import ChooseDatesReportComponent from "./chooseDates/choose-dates";
import { EditApplicationDialog, EditApplicationComponent } from "./edit/edit-application";

import {
  ApplicationsContext,
  ApplicationsProvider,
} from "./applications-context";

import pubSub, { EVENTS } from "../../config/pubSub";

// Styled components
const StyledContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1, 1),
  height: "100%",
}));

const StyledContent = styled(Box)(({ theme }) => ({
  maxWidth: "100%",
  padding: theme.spacing(4),
  flexGrow: 1,
  flexShrink: 0,
  flexBasis: "400px",
  borderRadius: "10px",
  boxShadow: "0px 3px 15px #00000029",
  [theme.breakpoints.between("xs", "sm")]: { maxWidth: "300px" },
  [theme.breakpoints.between("sm", "md")]: { maxWidth: "500px" },
  [theme.breakpoints.between("md", "lg")]: { maxWidth: "750px" },
  [theme.breakpoints.between("lg", "xl")]: { maxWidth: "1175px" },
  [theme.breakpoints.up("xl")]: { maxWidth: "100%" },
}));

const StyledToolbar = styled(Box)(({ theme }) => ({
  margin: theme.spacing(0, 0, 3, 0),
}));

// Applications List Section
const ApplicationsListPage = () => {
  const { appListTab } = useContext(ApplicationsContext);
  const { t } = useTranslation();

  return (
    <StyledContainer display="flex" flexDirection="column">
      <StyledContent>
        <header style={{ marginBottom: 15 }}>
          <Typography variant="h4">{t("page_application_title")}</Typography>
        </header>

        <StyledToolbar display="flex" flexWrap="wrap">
          <AddApplicationComponent />
          <EditApplicationComponent />
          <DeleteApplicationComponent />
          <ChooseDatesReportComponent />
        </StyledToolbar>

        <ApplicationListComponent statusFilter={appListTab} />
      </StyledContent>
    </StyledContainer>
  );
};

// Main Page with Provider and Dialog Handling
const ApplicationsPage = () => {
  const { selectedApp, setSelectedApp, setSelectedCheckedApps } = useContext(ApplicationsContext);
  const [openEditDialog, setOpenEditDialog] = useState(false);

  useEffect(() => {
    const isEditEnabled = !!selectedApp && selectedApp.split(",").length === 1;
    setOpenEditDialog(isEditEnabled);
  }, [selectedApp]);

  const handleCloseEdit = () => {
    setOpenEditDialog(false);
    setSelectedApp(null);
    setSelectedCheckedApps([]);
    pubSub.publish(EVENTS.ORGANIZATIONS_LIST);
  };

  const handleEditDone = () => {
    setOpenEditDialog(false);
    setSelectedApp(null);
    setSelectedCheckedApps([]);
    pubSub.publish(EVENTS.ORGANIZATIONS_LIST);
  };

  return (
    <ApplicationsProvider>
      <Box display="flex" flexDirection="column">
        <ApplicationsListPage />
        {selectedApp && (
          <EditApplicationDialog
            open={openEditDialog}
            selectedApp={selectedApp}
            handleEditApplicationClose={handleCloseEdit}
            handleEditApplicationDone={handleEditDone}
          />
        )}
      </Box>
    </ApplicationsProvider>
  );
};

export default ApplicationsPage;
